/**
 * PWAInstallBanner — bannière élégante en bas d'écran proposant d'installer
 * Helencia comme app.
 *
 * Comportement :
 *  - S'affiche seulement si l'app peut être installée (Chrome, Android, iOS
 *    Safari) ET si l'user ne l'a pas déjà refusée pendant 30 jours (stocké
 *    dans localStorage).
 *  - Adapté au design Helencia : Liquid Glass, caramel, subtile.
 *  - Sur iOS : ouvre une modal expliquant "Partager → Ajouter à l'écran
 *    d'accueil" (pas de prompt natif possible).
 *  - Sur Chromium : bouton "Installer" qui déclenche le prompt natif.
 *  - Fermable via un ✕ (mémorisé 30 jours).
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Smartphone, Share, Plus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { usePWAInstall } from "../hooks/usePWAInstall";

const DISMISS_KEY = "helencia_pwa_dismissed_at";
const DISMISS_DAYS = 30;

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export default function PWAInstallBanner() {
  const { canInstall, platform, install } = usePWAInstall();
  const [dismissed, setDismissed] = useState(wasDismissedRecently());
  const [iosOpen, setIosOpen] = useState(false);

  // Petit délai avant de montrer la bannière pour ne pas polluer le premier paint
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!canInstall || dismissed) return;
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [canInstall, dismissed]);

  if (!canInstall || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (platform === "ios") {
      setIosOpen(true);
      return;
    }
    const outcome = await install();
    if (outcome === "accepted" || outcome === "dismissed") {
      // Chrome ne reproposera pas — on masque quoi qu'il arrive
      handleDismiss();
    }
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 80 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[80] bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm"
            data-testid="pwa-install-banner"
          >
            <div className="relative rounded-3xl border border-[#EADDC9] bg-white/85 backdrop-blur-xl shadow-2xl p-4 pr-11">
              <button
                onClick={handleDismiss}
                aria-label="Fermer"
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#FAF3EA] text-[#6E4B2A] flex items-center justify-center hover:bg-[#EADDC9] transition-colors"
                data-testid="pwa-install-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#C68E59] to-[#A26F3E] text-white flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[15px] text-[#1A1A1A] leading-tight">
                    Installez Helencia
                  </p>
                  <p className="text-[11.5px] text-[#6E4B2A] mt-0.5 leading-snug">
                    Une icône sur votre écran d'accueil, plus rapide qu'un navigateur.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleInstall}
                className="mt-3 w-full h-10 rounded-full bg-[#1A1A1A] hover:bg-[#3C2C1D] text-white text-[13px] font-medium"
                data-testid="pwa-install-cta"
              >
                {platform === "ios" ? "Voir comment installer" : "Installer maintenant"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS Safari : pas de prompt natif → modal d'instructions manuelles */}
      <AnimatePresence>
        {iosOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
            onClick={() => setIosOpen(false)}
            data-testid="pwa-ios-modal"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl bg-white p-6 pt-8"
            >
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C68E59] to-[#A26F3E] text-white flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-7 h-7" />
                </div>
                <p className="caption-label text-[#A26F3E]">Installation sur iPhone</p>
                <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">
                  Ajouter à l'écran d'accueil
                </h3>
              </div>

              <ol className="mt-6 space-y-4">
                <li className="flex gap-3 items-start">
                  <span className="w-7 h-7 rounded-full bg-[#FAF3EA] text-[#A26F3E] flex items-center justify-center text-xs font-semibold flex-shrink-0">1</span>
                  <span className="text-sm text-[#3C2C1D] leading-relaxed">
                    Appuyez sur l'icône <Share className="inline w-4 h-4 mx-1 -mt-0.5 text-[#0A84FF]" /> <strong>Partager</strong> en bas de Safari.
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="w-7 h-7 rounded-full bg-[#FAF3EA] text-[#A26F3E] flex items-center justify-center text-xs font-semibold flex-shrink-0">2</span>
                  <span className="text-sm text-[#3C2C1D] leading-relaxed">
                    Faites défiler et choisissez <Plus className="inline w-4 h-4 mx-1 -mt-0.5" /> <strong>Sur l'écran d'accueil</strong>.
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="w-7 h-7 rounded-full bg-[#FAF3EA] text-[#A26F3E] flex items-center justify-center text-xs font-semibold flex-shrink-0">3</span>
                  <span className="text-sm text-[#3C2C1D] leading-relaxed">
                    Appuyez sur <strong>Ajouter</strong> — Helencia apparaîtra sur votre écran d'accueil comme une vraie app.
                  </span>
                </li>
              </ol>

              <Button
                onClick={() => {
                  setIosOpen(false);
                  handleDismiss();
                }}
                className="mt-6 w-full h-11 rounded-full bg-[#1A1A1A] hover:bg-[#3C2C1D] text-white"
                data-testid="pwa-ios-close"
              >
                J'ai compris
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
